/**
 * "Show QR code" beside a pool's invite link, and the scan view it opens
 * (ISSUE-1339). v1 showed the pool link as a QR code with "Scan QR code to
 * access pool" and offered the code as a picture. Here a fan holds their
 * screen up to a friend, or saves the picture to text it or print it.
 *
 * The overlay is the shared `Dialog`, so it is a bottom sheet on a phone and
 * a centered dialog on a desktop (`responsive-overlays.mdc`).
 */
import { Button, Dialog, InlineBanner, QrCode } from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import { saveQrImage } from "./poolQrImage";

export const SHOW_QR_LABEL = "Show QR code";
export const QR_DIALOG_DESCRIPTION =
  "Point a phone camera at this to open the pool.";
export const SAVE_QR_LABEL = "Save QR code";
export const QR_SAVE_FAILED_MESSAGE =
  "We couldn't make the picture. Take a screenshot instead.";

export type typePoolQrButtonProps = {
  /** The link the code opens: the pool's invite link or your own share link. */
  inviteUrl: string;
  poolName: string;
  "data-testid"?: string | undefined;
};

export const PoolQrButton = ({
  inviteUrl,
  poolName,
  "data-testid": testId = "pool-qr",
}: typePoolQrButtonProps): ReactNode => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasSaveFailed, setHasSaveFailed] = useState(false);

  async function handleSave(): Promise<void> {
    setIsSaving(true);
    setHasSaveFailed(false);
    const outcome = await saveQrImage({ value: inviteUrl, poolName });
    setIsSaving(false);
    setHasSaveFailed(outcome === "failed");
  }

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => {
          setHasSaveFailed(false);
          setIsOpen(true);
        }}
        data-testid={`${testId}-open`}
      >
        {SHOW_QR_LABEL}
      </Button>
      <Dialog
        open={isOpen}
        onOpenChange={setIsOpen}
        title={`Scan to join ${poolName}`}
        description={QR_DIALOG_DESCRIPTION}
        footer={
          <Button
            variant="primary"
            loading={isSaving}
            onClick={() => void handleSave()}
            data-testid={`${testId}-save`}
          >
            {SAVE_QR_LABEL}
          </Button>
        }
      >
        <div className="flex flex-col items-center gap-3">
          <QrCode
            value={inviteUrl}
            label={`QR code for ${poolName}`}
            size="lg"
            data-testid={`${testId}-code`}
          />
          <p className="text-caption text-ink-secondary break-all text-center">
            {inviteUrl}
          </p>
          {hasSaveFailed ? (
            <InlineBanner variant="warning" message={QR_SAVE_FAILED_MESSAGE} />
          ) : null}
        </div>
      </Dialog>
    </>
  );
};
